import { Stack, Group, Text, Badge, Divider, Progress, Grid, EmptyState } from '../../../../components/ui';
import { IconInfoCircle } from '@tabler/icons-react';
import { formatUptime } from '../utils/systemHelpers.tsx';
import type { SystemInfoTabProps } from '../types';

export function SystemInfoTab({ systemInfo }: SystemInfoTabProps) {
  if (!systemInfo) {
    return (
      <EmptyState
        icon={<IconInfoCircle size={64} />}
        title="No System Information"
        description="Unable to fetch system information at this time."
      />
    );
  }

  return (
    <Stack gap="md">
      <Group gap="sm">
        <IconInfoCircle size={20} />
        <Text size="md" fw={600}>
          System Information
        </Text>
      </Group>
      <Divider />

      {/* Basic Info Grid */}
      <Grid>
        <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase">
              Environment
            </Text>
            <Badge size="lg" variant="light">
              {systemInfo.environment}
            </Badge>
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase">
              Version
            </Text>
            <Text size="sm" fw={600}>
              {systemInfo.version}
            </Text>
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase">
              Uptime
            </Text>
            <Text size="sm" fw={600}>
              {formatUptime(systemInfo.uptime)}
            </Text>
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase">
              Machine Name
            </Text>
            <Text size="sm">{systemInfo.machineName}</Text>
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase">
              OS Version
            </Text>
            <Text size="sm">{systemInfo.osVersion}</Text>
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase">
              .NET Version
            </Text>
            <Text size="sm">{systemInfo.dotnetVersion}</Text>
          </Stack>
        </Grid.Col>
      </Grid>

      {/* Memory Usage */}
      {systemInfo.memoryUsage && (
        <>
          <Divider />
          <Stack gap="xs">
            <Group justify="space-between">
              <Text size="sm" fw={600}>
                Memory Usage
              </Text>
              <Text size="sm">
                {systemInfo.memoryUsage.used.toFixed(2)} MB / {systemInfo.memoryUsage.total.toFixed(2)} MB
              </Text>
            </Group>
            <Progress
              value={systemInfo.memoryUsage.percentage}
              color={systemInfo.memoryUsage.percentage > 80 ? 'red' : 'blue'}
            />
            <Text size="xs" c="dimmed" ta="right">
              {systemInfo.memoryUsage.percentage.toFixed(1)}%
            </Text>
          </Stack>
        </>
      )}

      {/* CPU Usage */}
      {systemInfo.cpuUsage !== undefined && (
        <>
          <Divider />
          <Stack gap="xs">
            <Group justify="space-between">
              <Text size="sm" fw={600}>
                CPU Usage
              </Text>
              <Text size="sm">{systemInfo.cpuUsage.toFixed(1)}%</Text>
            </Group>
            <Progress value={systemInfo.cpuUsage} color={systemInfo.cpuUsage > 80 ? 'red' : 'green'} />
          </Stack>
        </>
      )}
    </Stack>
  );
}
